import type { AccommodationQuickView, AccommodationQuickViewInclusion } from 'voyager';

import { getRepeatableExperienceOccurrenceDays } from '@features/DynamicPackagesConfigurationWizard/helpers/repeatableExperiencesUtils';
import {
  getPackageInclusions,
  inclusionLabelKeys,
  isSpaExperience,
  isSpaExtra,
  isSpaTreatment,
  PackageExperience,
} from '@features/packages/utils/getPackageInclusions';
import { TranslationsFunc } from '@lang';
import { Rate } from '../types';

const INCLUSION_SEPARATOR = ' — ';

const toInclusion = (
  id: string,
  groupLabel: string,
  detail?: string | null
): AccommodationQuickViewInclusion => ({
  id,
  label: detail ? `${groupLabel}${INCLUSION_SEPARATOR}${detail}` : groupLabel,
});

const dedupeByLabel = (inclusions: AccommodationQuickViewInclusion[]) => {
  const seen = new Set<string>();
  return inclusions.filter(({ label }) => {
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
};

const getPackageElements = (rate: Rate) => {
  const ratePackage = rate.package;
  return {
    restaurants:
      ratePackage && Array.isArray(ratePackage.restaurants) ? ratePackage.restaurants : [],
    experiences:
      ratePackage && Array.isArray(ratePackage.experiences) ? ratePackage.experiences : [],
  };
};

const getMainOptions = (experience: PackageExperience) =>
  (Array.isArray(experience.options) ? experience.options : []).filter(
    (option) => !isSpaExtra(option)
  );

const isChoice = (experience: PackageExperience) =>
  experience.numberOfSelectionsRequired > 0 && getMainOptions(experience).length > 1;

const isIncludedSet = (experience: PackageExperience) =>
  !isChoice(experience) && getMainOptions(experience).length > 1;

export const getRateInclusions = (
  rate: Rate,
  t: TranslationsFunc,
  stayNights = 1
): AccommodationQuickViewInclusion[] => {
  const categories = getPackageInclusions(rate.package);
  if (categories.length === 0) return [];

  const { restaurants, experiences } = getPackageElements(rate);
  const spaExperiences = categories.includes('spa') ? experiences.filter(isSpaExperience) : [];

  const spa = spaExperiences
    .filter((experience) => !isChoice(experience))
    .flatMap((experience, index) => {
      const mainOptions = getMainOptions(experience);
      const id = `spa-${experience.id ?? index}`;
      if (mainOptions.length === 1) {
        return [toInclusion(id, t(inclusionLabelKeys.spa), mainOptions[0].name)];
      }
      return mainOptions.every(isSpaTreatment) ? [] : [toInclusion(id, t(inclusionLabelKeys.spa))];
    });

  const isRepeating = rate.package?.numberOfNights === 1;
  const stayLength = Math.max(1, stayNights || 1);
  const occurrences = (dayNumber: number) =>
    isRepeating
      ? getRepeatableExperienceOccurrenceDays({
          experienceDayNumber: dayNumber,
          stayDuration: stayLength,
        }).length
      : 1;
  const countChoices = (groups: PackageExperience[]) =>
    groups.reduce(
      (total, group) =>
        total + group.numberOfSelectionsRequired * occurrences(group.dayNumber ?? 0),
      0
    );

  const choices = spaExperiences.filter(isChoice);
  const treatmentChoices = choices.filter((experience) =>
    getMainOptions(experience).every(isSpaTreatment)
  );
  const otherChoices = choices.filter((experience) => !treatmentChoices.includes(experience));
  const includedTreatments = spaExperiences
    .filter(isIncludedSet)
    .reduce(
      (total, group) =>
        total +
        getMainOptions(group).filter(isSpaTreatment).length *
          occurrences(group.dayNumberRange?.[0] ?? group.dayNumber ?? 0),
      0
    );

  const counted = [
    {
      id: 'spa-treatments',
      key: 'ratesListing.quickView.spaTreatments' as const,
      count: includedTreatments + countChoices(treatmentChoices),
    },
    {
      id: 'spa-experiences',
      key: 'ratesListing.quickView.experiences' as const,
      count: countChoices(otherChoices),
    },
  ]
    .filter(({ count }) => count > 0)
    .map(({ id, key, count }) => ({ id, label: t(key, { count }) }));

  const dining = categories.includes('dining')
    ? restaurants.map((restaurant, index) =>
        toInclusion(
          `dining-${restaurant.id ?? restaurant.restaurantId ?? index}`,
          t(inclusionLabelKeys.dining),
          restaurant.name ||
            (restaurant.additionalRestaurants?.length ? undefined : restaurant.restaurantName)
        )
      )
    : [];

  return dedupeByLabel([...spa, ...counted, ...dining]);
};

/**
 * A rate only earns a quick view when its package bundles something beyond the room —
 * a single-element rate has nothing extra to preview.
 */
export const getRateQuickView = (
  rate: Rate,
  t: TranslationsFunc,
  stayNights = 1
): AccommodationQuickView | undefined => {
  if (getPackageInclusions(rate.package).length === 0) return undefined;

  const inclusions = getRateInclusions(rate, t, stayNights);

  const images = (Array.isArray(rate.images) ? rate.images : [])
    .filter(Boolean)
    .map((src, index) => ({ src, alt: `${rate.title} ${index + 1}` }));

  const payablePrice = rate.discount?.discountedPrice ?? rate.price;
  const pricePerStay =
    payablePrice && !rate.unAvailable
      ? { amount: payablePrice.amount, currencyCode: payablePrice.currencyCode }
      : undefined;

  return {
    images: images.length > 0 ? images : undefined,
    description: rate.description || undefined,
    inclusions,
    pricePerStay,
  };
};
